import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CoinLetterTemplate } from '@coin/shared/cms/util';
import { CommunicationStateEnum } from '@coin/shared/communication/util';
import { EditDataTypesEnum } from '@coin/shared/employee-data/util';
import { WorkflowType } from '@coin/shared/core/util-enums';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { HeadcountSettingDropdownItemsType } from '../../enums/headcount-setting-dropdown-items-type.enum';
import { HeadcountSettingsWorkflowTemplateTypeEnum } from '../../enums/headcount-settings-request-state.enum';
import {
  HeadcountApprovalAdminSettingsResponse,
  HeadcountSettingConfig,
  HeadcountSettingContent
} from '../../interfaces/headcount-approval-admin-settings.model';
import { HeadcountApprovalAdminSettingsService } from '../../services/headcount-approval-admin-settings.service';
import { HEADCOUNT_ADMIN_SETTINGS } from './headcount-settings.config';

interface Workflow {
  id: string;
  workflowType: string;
  fromLetterState: string;
  toLetterState: string;
  forwardAfterDays: number;
}

@Component({
  selector: 'coin-a-headcount-settings',
  templateUrl: './headcount-settings.component.html',
  styleUrls: ['./headcount-settings.component.scss'],
  standalone: false
})
export class HeadcountSettingsComponent implements OnInit {
  private toast = inject(ToastrService);
  private headcountService = inject(HeadcountApprovalAdminSettingsService);
  private destroyRef = inject(DestroyRef);

  public managerMails: CoinLetterTemplate[] = [];
  public editDataTypesEnum = EditDataTypesEnum;
  public settingConfig: HeadcountSettingConfig;
  public headcountSettings: HeadcountApprovalAdminSettingsResponse;
  public workflows: Workflow[] = [];
  public emptyValue = null;

  public preNavigateBack(): Observable<string> {
    return of('/');
  }

  ngOnInit(): void {
    this.settingConfig = HEADCOUNT_ADMIN_SETTINGS;
    this.loadWorkflow();
    this.loadSettings();
  }

  public isQuestionnaireForGermanWithPartialEvaluationBoolean(item: HeadcountSettingContent): boolean {
    return item.type === EditDataTypesEnum.BOOLEAN;
  }

  private convertTimespanToDays(timespan: string): number {
    const daysPart = timespan.split(':')[0].split('.')[0];
    return parseInt(daysPart, 10);
  }

  public loadSettings(): void {
    this.headcountService
      .getAdminSettings()
      .pipe(
        tap(data => {
          for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
              const value = data[key];
              if (typeof value === 'string' && /^[0-9]+\.([0-9]{2}:){2}[0-9]{2}$/.test(value)) {
                const days = this.convertTimespanToDays(value);
                data[key] = days;
              }
            }
          }
        }),
        tap(data => {
          this.headcountSettings = data;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public getDropdownItems(item: HeadcountSettingContent): (string | CoinLetterTemplate)[] {
    switch (item.dropdownType) {
      case HeadcountSettingDropdownItemsType.Mail:
        return this.managerMails;
      default:
        return item.dropdownValues;
    }
  }

  public getDropdownBindedObjectProperty(type: HeadcountSettingDropdownItemsType): string {
    switch (type) {
      case HeadcountSettingDropdownItemsType.Mail:
        return 'id';
      default:
        return null;
    }
  }

  public displayFn(item: CoinLetterTemplate | string): string {
    return typeof item === 'string' ? item : item.title;
  }

  public save(): void {
    for (const key in this.headcountSettings) {
      if (Object.prototype.hasOwnProperty.call(this.headcountSettings, key)) {
        const value = this.headcountSettings[key];
        if (typeof value === 'number') {
          const timespan = this.convertDaysToTimespan(value);
          this.headcountSettings[key] = timespan;
        }
      }
    }
    this.headcountService
      .updateAdminSettings(this.headcountSettings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadSettings();
        this.toast.success('Updated Settings Successfully.');
      });
  }

  private loadWorkflow(): void {
    this.headcountService
      .getWorkflows(WorkflowType.Standard)
      .pipe(
        map(workflows => workflows.filter(workflow => workflow.type === 'NoActionNeeded').map(workflow => workflow.id)),
        switchMap(workflowIds =>
          forkJoin(
            workflowIds.map(workflowId =>
              this.headcountService.getFilteredWorkflowTemplates(
                HeadcountSettingsWorkflowTemplateTypeEnum.ManagerMail,
                workflowId
              )
            )
          )
        ),
        tap(templates => {
          this.managerMails = templates.flat().filter(item => item?.state === CommunicationStateEnum.Published);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private convertDaysToTimespan(days: number): string {
    return `${days}.00:00:00`;
  }
}
